import { Dispatch, SetStateAction, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// local imports - types
import warningImage from 'assets/images/warning-image.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import { IFilteredCoiffeurList, IFilteredCoiffeurSalonList } from 'types/CoiffeurType'
import DateUtil from 'utils/DateUtil'
import Loader from 'components/ui/Loader'
import NotificationDialog from 'components/ui/NotificationDialog'
import useGetCoiffeurReservationTable from 'components/home/on-campus-services/coiffeur/hooks/use-get-coiffeur-reservation-table'
import ReservationModalHeader from 'components/home/on-campus-services/coiffeur/reservation-scheduler/ReservationModalHeader'
import WeekSlider from 'components/home/on-campus-services/health-service/appointment-scheduler/WeekSlider'
import ReservationScheduler from 'components/home/on-campus-services/coiffeur/reservation-scheduler/ReservationScheduler'
import CoiffeurScheduleSidebar from 'components/home/on-campus-services/coiffeur/reservation-scheduler/ScheduleSidebar'
import {
  ActionArea,
  CancelButton,
  ConfirmButton,
} from 'components/home/on-campus-services/coiffeur/reservation-scheduler/styled'

/* Types */
interface IReservationModalProps {
  salon: IFilteredCoiffeurSalonList | undefined
  subServiceId: number
  locationId: number
  filterDate: Date
  coiffeurs: IFilteredCoiffeurList[] | []
  selectedCoiffeur: IFilteredCoiffeurList
  setSelectedCoiffeur: Dispatch<SetStateAction<any | undefined>>
  open: boolean
  cancelReservationModal: () => void
  showNotification: () => void
  closeReservationModal: () => void
  openReservationModal: () => void
  selectedDateStr?: string
}

interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()
  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.book-reservation')} onClick={onSave} />
    </ActionArea>
  )
}

export default function CalendarCoiffeurReservationModal({
  salon,
  filterDate: dateFromList,
  subServiceId,
  locationId,
  coiffeurs,
  selectedCoiffeur,
  setSelectedCoiffeur,
  open,
  cancelReservationModal,
  closeReservationModal,
  openReservationModal,
  showNotification,
  selectedDateStr,
}: IReservationModalProps) {
  const { t } = useTranslation()

  /* Coiffeur List reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  const [diffDayNotification, setDiffDayNotification] = useState(false)
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)
  const isSelectedDifferentDate = moment(filterDate).isSame(dateFromList, 'day')

  /* GET Selected Coiffeur's Reservation Table List | Custom Hook  */
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedReservation,
    setSelectedReservation,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyReservations,
  } = useGetCoiffeurReservationTable(selectedCoiffeur.id, subServiceId, dateFromList, locationId, true)

  /* Open Reservation Sidebar */
  const openAppointmentSidebar = () => {
    if (isSelectedDifferentDate) {
      setSidebarVisible(true)
      closeReservationModal()
    } else {
      closeReservationModal()
      setDiffDayNotification(true)
    }
  }

  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    showNotification()
    setSidebarVisible(false)
    closeReservationModal()
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => {
    cancelReservationModal()
    setFilterDate(dateFromList)
    setSelectedReservation(undefined)
    setTableWeek(lastFilteredDate)
  }

  /* Different Day Reservation - Notification Dialog Methods */
  const handleAcceptDiffDayReservation = () => {
    setDiffDayNotification(false)
    setSidebarVisible(true)
    closeReservationModal()
  }
  const handleRejectDiffDayReservation = () => {
    setDiffDayNotification(false)
    openReservationModal()
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        closeOnEscape={false}
        closable={true}
        focusOnShow={false}
        header={<span>{t('dialog-texts.coiffeur-reservation-header')} (2/2)</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '95vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openAppointmentSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!selectedReservation}
          />
        }>
        {/* Modal Header */}
        {!!salon && (
          <ReservationModalHeader
            selectedDateStr={selectedDateStr}
            subServiceId={subServiceId}
            salon={salon}
            filterDate={filterDate}
            availableDateRange={availableDates}
            setFilterDate={setFilterDate}
            setSelectedCoiffeur={setSelectedCoiffeur}
            selectedCoiffeur={selectedCoiffeur}
            coiffeurs={coiffeurs}
            setTableWeek={setTableWeek}
          />
        )}

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <ReservationScheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyReservations={dailyReservations}
            selectedReservation={selectedReservation}
            setSelectedReservation={setSelectedReservation}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedReservation && !!salon && (
        <CoiffeurScheduleSidebar
          salon={salon}
          subServiceId={subServiceId}
          openModal={openReservationModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          coiffeurInfo={selectedCoiffeur}
          reservationInfo={selectedReservation}
        />
      )}

      <NotificationDialog
        display={diffDayNotification}
        imgSrc={warningImage}
        goHomeButton={true}
        extraButton={true}
        title={t('overlaypanel-texts.reservation-day-changed')}
        content={t('overlaypanel-texts.reservation-day-change-text')}
        firstButtonLabel={t('buttons.yes')}
        secondButtonLabel={t('buttons.no')}
        onHide={handleRejectDiffDayReservation}
        onSubmit={handleAcceptDiffDayReservation}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}
